import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import { BodyM, BodyS, DisplayM } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import classNames from 'classnames';
import CustomInput from 'components/CustomInput/CustomInput';
import React, { useState } from 'react';
import numeral from 'numeral';
import styles from 'assets/jss/material-dashboard-pro-react/views/payments/topupStyle';
import { makeStyles } from '@material-ui/styles';
import TopUpValues from './subcomponents/TopUpValues';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { PAYMENT_METHODS } from 'views/PrepaidLoyalty/commons/constants';
import { MAX_CREDIT_LIMIT } from 'utils/const/services';
import LimitExceededCard from '../LimitExceededCard';
import { STEPS } from 'views/TopUpAddOns/TopUp';
import ChangeCreditLimitDialog from '../ChangeCreditLimitDialog';
import { CorpUserData } from 'utils/api/models/users/corpUserData';

// @ts-ignore
const useStyles = makeStyles(styles);

interface ITopUpCardProps {
	isEmpty: boolean;
	setIsEmpty: React.Dispatch<React.SetStateAction<boolean>>;
	topUpValue?: number;
	creditLimit?: string;
	masterNumber?: string;
	userData?: CorpUserData;
	accountNumber?: string;
	setCreditLimit?: React.Dispatch<React.SetStateAction<string>>;
	paymentMethod?: number;
	setTopUpValue?: React.Dispatch<React.SetStateAction<number>>;
	setActiveTab?: React.Dispatch<React.SetStateAction<number>>;
	customTopUpValue: number;
	selectedNumbers: types.topUpAddOns.INumber[];
	totalAmount: number;
	accountDetails?: string;
	closeEdit: () => void;
	setCustomTopUpValue: (value: number) => void;
	checkTopUpValue: (value: string, setIsEmpty: Function, setValue: (value: number) => void) => void;
}

const TopUpCard = ({
	totalAmount,
	creditLimit,
	accountDetails,
	setCreditLimit = () => {},
	paymentMethod,
	isEmpty,
	topUpValue,
	masterNumber,
	userData,
	accountNumber,
	setTopUpValue,
	setActiveTab,
	customTopUpValue,
	selectedNumbers,
	setIsEmpty,
	closeEdit,
	setCustomTopUpValue,
	checkTopUpValue,
}: ITopUpCardProps) => {
	const classes = useStyles();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const showCreditLimitExceed: boolean =
		paymentMethod === PAYMENT_METHODS.BY_MASTER_NUMBER && Number(creditLimit) < totalAmount;

	const showCreditLimitButton: boolean = !(Number(creditLimit) === MAX_CREDIT_LIMIT);
	const itemsToChange: types.topUpAddOns.ItemToChange[] = [
		{ text: 'Top up amount / Numbers' },
		{
			text: 'Payment Method',
			onClick: () => {
				setActiveTab && setActiveTab(STEPS.PAYMENT_METHOD);
			},
		},
		...(showCreditLimitButton ? [{ text: 'Credit Limit', onClick: () => setIsOpen(true) }] : []),
	];

	return (
		<div className={classes.topupCard}>
			<Card>
				<CardBody>
					<GridContainer justify="space-between" className={classes.headerContainer}>
						<GridItem xs={12} sm={12} md={4} lg={4} id="topup-value">
							<TopUpValues topUpValue={topUpValue} setTopUpValue={setTopUpValue} />
						</GridItem>
						<ConditionalRender
							show={false}
							onTrue={() => (
								<GridItem xs={12} sm={6} md={6} className={classes.alignRight}>
									<BodyS color="secondary-light">
										{translate('payments.topup.customTopupValue')}
									</BodyS>
									<CustomInput
										helperText={translate('topup.value.limit')}
										id="topup-value-input"
										inputProps={{
											value: isEmpty ? '' : customTopUpValue,
											onChange: (event: React.FormEvent<HTMLInputElement>) => {
												const value = (event.target as HTMLInputElement).value;
												closeEdit();
												checkTopUpValue(value, setIsEmpty, (value: number) => {
													setCustomTopUpValue(Number(value));
													setTopUpValue?.(Number(value));
												});
											},
										}}
										formControlProps={{
											className: classNames(classes.customTopUpInput, classes.customInput),
										}}
										variant="outlined"
									/>
								</GridItem>
							)}
						/>
						<GridItem xs={12} sm={12} md={3} className={classes.alignRight} id="selected-numbers">
							<BodyS color="secondary-light">{translate('payments.topup.selectedNumbers')}</BodyS>
							<div className={classes.marginValue}>
								<DisplayM color="disabled-light" disableMargin>
									{String(selectedNumbers.length)}
								</DisplayM>
							</div>
						</GridItem>
						<GridItem xs={12} sm={12} md={4} id="total-amount">
							<BodyS color="secondary-light">{translate('payments.topup.totalAmount')}</BodyS>
							<div
								className={classNames(classes.marginValue, classes.totalAmountContainer, {
									[classes.dangerColor]: showCreditLimitExceed,
								})}
							>
								<DisplayM inline>{numeral(totalAmount).format('0,0') + translate('OMR')}</DisplayM>
								<ConditionalRender
									show={paymentMethod === PAYMENT_METHODS.BY_MASTER_NUMBER}
									onTrue={() => (
										<>
											<div className={classes.divider}></div>
											<div className={classes.creditLimitContainer}>
												<BodyM inline disableMargin color="disabled-light">
													{creditLimit + translate('OMR')}
												</BodyM>
												<BodyS disableMargin color="disabled-light">
													{translate('top.up.add.ons.credit.limit')}
												</BodyS>
											</div>
										</>
									)}
								/>
							</div>
						</GridItem>
					</GridContainer>
					<ConditionalRender
						show={showCreditLimitExceed}
						onTrue={() => <LimitExceededCard itemsToChange={itemsToChange} />}
					/>
				</CardBody>
			</Card>
			<ChangeCreditLimitDialog
				isOpen={isOpen}
				hideDialog={() => setIsOpen(false)}
				phoneNumber={masterNumber || ''}
				accountDetails={accountDetails || ''}
				accountNumber={accountNumber || ''}
				creditLimit={creditLimit || ''}
				setCreditLimit={setCreditLimit}
				userData={userData}
			/>
		</div>
	);
};

export default TopUpCard;
